import React, { useState, useEffect } from 'react';
import './Preloader.css';

/*
 * Branded intro loader featuring the brand logo, framed by orbiting electrons
 * (atom / physics) over a blueprint grid, with a scan line that sweeps across
 * the logo (engineering blueprint reveal). Shows once per browser session, then
 * fades to reveal the site. Reduced-motion: static logo + brief fade.
 */
export default function Preloader() {
  const [done, setDone] = useState(() => {
    try {
      return sessionStorage.getItem('moab-intro') === '1';
    } catch {
      return false;
    }
  });
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (done) return undefined;
    document.body.style.overflow = 'hidden';
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const hold = reduce ? 700 : 2600;
    const t1 = setTimeout(() => setLeaving(true), hold);
    const t2 = setTimeout(() => {
      try {
        sessionStorage.setItem('moab-intro', '1');
      } catch {
        /* ignore */
      }
      document.body.style.overflow = '';
      setDone(true);
    }, hold + 700);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      document.body.style.overflow = '';
    };
  }, [done]);

  if (done) return null;

  return (
    <div className={`preloader${leaving ? ' is-leaving' : ''}`} role="status" aria-label="Loading">
      <div className="preloader__grid" aria-hidden="true" />

      <div className="preloader__stage">
        <svg className="pl-atom" viewBox="0 0 520 300" aria-hidden="true">
          {[0, 60, 120].map((rot, i) => (
            <g key={rot} transform={`rotate(${rot} 260 150)`}>
              <ellipse className="pl-orbit" cx="260" cy="150" rx="230" ry="62" />
              <circle className="pl-electron" r="4.2">
                <animateMotion
                  dur={`${2.4 + i * 0.5}s`}
                  repeatCount="indefinite"
                  path="M30,150 a230,62 0 1,0 460,0 a230,62 0 1,0 -460,0"
                />
              </circle>
            </g>
          ))}
          <circle className="pl-nucleus" cx="260" cy="150" r="5" />
        </svg>

        <div className="pl-logo-wrap">
          <img className="pl-logo" src="/assets/logo/logo.png" alt="Moab Planning & Civil Engineering" />
          <span className="pl-scan" aria-hidden="true" />
        </div>
      </div>

      <div className="preloader__bar" aria-hidden="true">
        <span />
      </div>
    </div>
  );
}
